import { useT } from '@/core/I18nProvider';

import { Frame } from './Frame';
import { useSteppedLoop } from './use-stepped-loop';

import type { ReactElement } from 'react';

/**
 * "Nothing is on until you turn it on" — the capability list, switching on one
 * row at a time.
 *
 * Said "the review queue" until Phase 1 of the walk-first program: ADR-0537
 * removed that page along with the drafters that fed it, so the visual named a
 * surface the product no longer has. What it actually depicts — a list of
 * capabilities, each off until an operator turns it on — is unchanged and still
 * shipped; only the name was wrong.
 *
 * ## The row that cannot be switched
 *
 * Four rows go on as the sequence runs and the fifth never does: it carries the
 * "always asks" lock instead of a toggle, because a capability the agent found
 * by looking at a screen confirms with the person every time, whatever the
 * workspace has configured (ADR-0400). That row is the reason this visual is
 * worth animating at all — a grid of switches says "configurable", and one
 * switch that is missing on purpose says "and this part is not yours to
 * weaken", which is the stronger claim.
 *
 * The fifth row is therefore rendered in its final state from the first frame.
 * Animating it would say the opposite of what it is there to say.
 */
const ROWS = ['upgrade', 'invite', 'export', 'address'] as const;

export function GovernVisual(): ReactElement {
  const { t } = useT();
  const { ref, step } = useSteppedLoop<HTMLDivElement>(ROWS.length + 2, 950);

  return (
    <div ref={ref} aria-hidden="true" data-features-visual>
      <Frame className="w-full">
        <div className="flex flex-col gap-2.5 p-5">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono rtl:font-sans text-[10px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
              {t('features.showcase.govern.listLabel')}
            </p>
            <p className="font-mono text-[0.74rem] tabular-nums text-[color:var(--bd-muted)]">
              <span dir="ltr">{Math.min(step, ROWS.length)}/5</span>
            </p>
          </div>

          <ul className="flex flex-col gap-2">
            {ROWS.map((id, i) => {
              const on = i < step;
              return (
                <li
                  key={id}
                  className="flex items-center gap-3 rounded-[9px] border border-[color:var(--bd-line-2)] px-3 py-2.5 transition-colors duration-500"
                  style={{
                    background: on ? 'rgb(var(--bd-blue-rgb) / 0.04)' : 'var(--bd-page)',
                    borderColor: on ? 'rgb(var(--bd-blue-rgb) / 0.22)' : 'var(--bd-line-2)',
                  }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.83rem] text-[color:var(--bd-ink)]">
                      {t(`features.showcase.govern.row.${id}.title`)}
                    </span>
                    <span className="block truncate font-mono text-[0.68rem] text-[color:var(--bd-subtle)]">
                      {t(`features.showcase.govern.row.${id}.meta`)}
                    </span>
                  </span>

                  {/* A switch. Track + knob, both driven off one boolean, so
                      there is no state in which the two disagree. */}
                  <span
                    className="relative block h-[19px] w-[34px] flex-none rounded-full transition-colors duration-500"
                    style={{
                      background: on ? 'var(--bd-gradient)' : 'var(--bd-line-3)',
                    }}
                  >
                    <span
                      className="absolute top-[2.5px] size-[14px] rounded-full bg-white shadow-sm transition-all duration-500"
                      style={{ insetInlineStart: on ? '17px' : '2.5px' }}
                    />
                  </span>
                </li>
              );
            })}

            {/* The locked row — final state from frame one, on purpose. */}
            <li className="flex items-center gap-3 rounded-[9px] border border-dashed border-[color:var(--bd-line-3)] px-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[0.83rem] text-[color:var(--bd-ink)]">
                  {t('features.showcase.govern.row.locked.title')}
                </span>
                <span className="block truncate font-mono text-[0.68rem] text-[color:var(--bd-subtle)]">
                  {t('features.showcase.govern.row.locked.meta')}
                </span>
              </span>
              <span className="flex flex-none items-center gap-1.5 rounded-full bg-[color:var(--bd-sunk)] px-2.5 py-1 text-[0.68rem] font-medium text-[color:var(--bd-muted)]">
                <svg viewBox="0 0 12 12" className="size-[10px]" fill="none">
                  <rect
                    x="2.6"
                    y="5.2"
                    width="6.8"
                    height="4.8"
                    rx="1.1"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M4.3 5.2V4a1.7 1.7 0 0 1 3.4 0v1.2"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
                {t('features.showcase.govern.alwaysAsks')}
              </span>
            </li>
          </ul>

          <p className="pt-1 text-[0.78rem] leading-[1.5] text-[color:var(--bd-muted)]">
            {t('features.showcase.govern.footnote')}
          </p>
        </div>
      </Frame>
    </div>
  );
}
